import { gql } from '@apollo/client';
import React, { createRef, useEffect, useMemo } from 'react';
import { Clickable } from '@moda/om';
import ExitIcon from '@moda/icons/exit-24';
import { ResponsiveImage } from '../ResponsiveImage';
import { ProductZoomedGalleryVariantFragment } from '../../generated/types';
import { Modal } from '../Modal';
import { ProductMediaGalleryThumbnail } from '../ProductMediaGallery/DesktopProductMediaGallery';

import './ProductZoomedGallery.scss';

type PrimaryImage = ProductZoomedGalleryVariantFragment['primaryImage'];

export const PRODUCT_ZOOMED_GALLERY_VARIANT_FRAGMENT = gql`
  fragment ProductZoomedGalleryVariantFragment on VariantData {
    id
    primaryImage: primary_image_urls {
      raw
      thumb
    }
    alternateImages: alternate_image_urls {
      raw
      thumb
    }
  }
`;

export const PRODUCT_ZOOMED_GALLERY_FRAGMENT = gql`
  fragment ProductZoomedGalleryFragment on Variant {
    id
    masterVariant: master_variants_data {
      ...ProductZoomedGalleryVariantFragment
    }
    otherVariants: other_variants_data {
      ...ProductZoomedGalleryVariantFragment
    }
  }
  ${PRODUCT_ZOOMED_GALLERY_VARIANT_FRAGMENT}
`;

interface Props {
  onClose(): void;
  images: PrimaryImage[];
  activeIndex: number;
  thumbnails: ProductMediaGalleryThumbnail[];
}

export const ProductZoomedGallery: React.FC<Props> = ({
  onClose,
  images,
  activeIndex,
  thumbnails
}) => {
  const imagesWithRef = useMemo(
    () =>
      images.map(image => ({
        image,
        ref: createRef<HTMLButtonElement>(),
        thumb: image.thumb
      })),
    [images]
  );

  useEffect(() => {
    const activeImage = imagesWithRef.find(item => item.thumb === thumbnails[activeIndex].thumb);

    if (!activeImage) return;

    activeImage.ref.current?.scrollIntoView();
  }, [activeIndex, imagesWithRef, thumbnails]);

  return (
    <Modal className="ProductZoomedGallery" onClose={onClose}>
      <Clickable
        className="ProductZoomedGallery__close"
        onClick={onClose}
        aria-label="Close zoomed view"
      >
        <ExitIcon aria-hidden="true" />
      </Clickable>

      <div className="ProductZoomedGallery__column">
        {imagesWithRef.map(
          ({ image, ref }) =>
            image &&
            image.raw && (
              <Clickable
                key={image.raw}
                className="ProductZoomedGallery__cell"
                ref={ref}
                onClick={onClose}
              >
                <ResponsiveImage
                  className="ProductZoomedGallery__image"
                  src={image.raw}
                  alt="Zoomed product view"
                  maxWidth={800}
                  maxHeight={1600}
                />
              </Clickable>
            )
        )}
      </div>
    </Modal>
  );
};
